import { useMemo, useState, useEffect } from 'react';
import {
  useComplianceAIReports,
  useKickComplianceEvaluation,
  type ComplianceAIReport,
  type ComplianceVerdict,
} from '../../../features/compliance/api';
import { useToast } from '../../../hooks/useToast';
import { brandConfig } from '../../../config/brands';
import styles from './CompliancePanel.module.css';

interface Props {
  materialId: string;
  materialTable: 'temporary_materials' | 'materials';
  /** generation_metadata._app.compliance.applied_rulesets (code → name 解決用) */
  appliedRulesets?: Array<{ code: string; name: string; severity?: string }>;
  /** 業界カテゴリ（未設定なら判定ボタンを disable する） */
  hasIndustryCategories: boolean;
}

const VERDICT_LABEL: Record<ComplianceVerdict, string> = {
  pass: '問題なし',
  warn: '要注意',
  block: '違反あり',
  error: '判定エラー',
};

export function CompliancePanel({ materialId, materialTable, appliedRulesets, hasIndustryCategories }: Props) {
  const toast = useToast();
  const { data: reports, isLoading } = useComplianceAIReports(materialId, materialTable);
  const kickMutation = useKickComplianceEvaluation();

  const latest: ComplianceAIReport | null = reports && reports.length > 0 ? reports[0] : null;

  const ruleNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of appliedRulesets || []) {
      if (r.code) map.set(r.code, r.name || r.code);
    }
    return map;
  }, [appliedRulesets]);

  const handleKick = async () => {
    try {
      await kickMutation.mutateAsync({ materialId, materialTable });
      toast.success(latest ? 'コンプライアンス判定を再実行しました' : 'コンプライアンス判定を実行しました');
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.response?.data?.error || '判定に失敗しました';
      toast.error(message);
    }
  };

  const disableKick = !hasIndustryCategories || kickMutation.isPending;

  const violated = latest?.violations.filter((v) => v.violated) || [];

  return (
    <div className={styles.panel}>
      {isLoading ? (
        <span className={styles.loading}>読み込み中...</span>
      ) : latest ? (
        <>
          <div className={styles.verdictRow}>
            <span className={`${styles.verdictBadge} ${styles[`verdict_${latest.overall_verdict}`]}`}>
              {VERDICT_LABEL[latest.overall_verdict]}
            </span>
            <span className={styles.meta}>
              {latest.model}
              {latest.evaluation_seconds != null ? ` · ${latest.evaluation_seconds.toFixed(1)}秒` : ''}
            </span>
          </div>
          {latest.error_message ? (
            <p className={styles.note}>判定に失敗しました: {latest.error_message}</p>
          ) : violated.length === 0 ? (
            <p className={styles.note}>違反は検出されませんでした</p>
          ) : (
            <div className={styles.violations}>
              {violated.map((v, i) => (
                <div key={`${v.rule_code}-${i}`} className={styles.violation}>
                  <div className={`${styles.ruleBadge} ${styles[`severity_${v.severity}`]}`} title={v.rule_code}>
                    {ruleNameByCode.get(v.rule_code) || v.rule_code}
                  </div>
                  {v.evidence && <p className={styles.evidence}>{v.evidence}</p>}
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            className="btn-pill-outline"
            onClick={handleKick}
            disabled={disableKick}
            style={{ marginTop: 8 }}
          >
            {kickMutation.isPending ? '再判定中...' : '再判定を実行'}
          </button>
        </>
      ) : hasIndustryCategories ? (
        <button type="button" className="btn-pill-outline" onClick={handleKick} disabled={disableKick}>
          {kickMutation.isPending ? '判定中...' : 'コンプラ判定を実行'}
        </button>
      ) : (
        <DisabledVerdictBadge />
      )}
    </div>
  );
}

function DisabledVerdictBadge() {
  const [open, setOpen] = useState(false);

  // 外側クリック / Escape で閉じる
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`.${styles.disabledWrap}`)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span className={`${styles.disabledWrap} ${open ? styles.disabledOpen : ''}`}>
      <span
        role="button"
        tabIndex={0}
        className={`${styles.verdictBadge} ${styles.verdict_disabled}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((s) => !s);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((s) => !s);
          }
        }}
      >
        判定不可
      </span>
      <span className={styles.disabledTooltip} role="tooltip">
        {brandConfig.labels.productShort}の特定カテゴリが設定されていないため判定できません。{brandConfig.labels.productShort}の「特定カテゴリ」を設定し、再生成してください。
      </span>
    </span>
  );
}
